import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchAdminInncodesAsync } from "components/features/MyPlans/myplans.slice";
import { store } from "redux/store";
import { axiosDelete, axiosFileUpload, axiosGet, axiosPatch, axiosPost } from "utils/APIHelper";
import { PAGE_SIZE, SME_PAGE_SIZE } from "utils/CommonConstants";
import { responseTypes, serviceTypes } from "utils/Types";

const initialState = {
  users: [],
  plans: [],
  planStats: [],
  status: "idle",
  dialogId: null,
  mappingId: null,
  mappedUsers: [],
  planDetails: [],
  innCodeStats: [],
  planStatsTotal: 0,
  smeShowLoader: false,
  innCodeStatsTotal: 0,
  showImportDialog: false,
  showDeleteDialog: false,
  showAddInncodeDialog: false,
  showImportFailDialog: false,
  showDeleteInncodeDialog: false,

  myLists: {
    records: [],
    count: 0
  },

  myListDetails: {
    id: null,
    mappings: [],
    name: "",
    createdDate: "",
    planAssigneeMap: [],
    assignedPlanCount: 0,
    lowEngagementCount: 0,
    highEngagementCount: 0,
    mediumEngagementCount: 0,
    overDueCardPlansCount: 0,
    innCodeGroupMappingCount: 0,
    unAssignedCardPlansCount: 0,
    meta: {
      page: { total: 0 },
      filters: []
    }
  },

  filters: {
    innCode: [],
    engagement: [],
    statsEngagement: [],
    planStatus: []
  }
};

export const fetchMyLists = createAsyncThunk(
  "myLists",
  async (data?: { searchText?: string; page?: number; size?: number }, thunkApi?: any) => {
    const API_URL = data?.searchText
      ? `inncode-groups?searchText=${data?.searchText}&page=${data?.page ?? 0}&size=${data?.size ?? SME_PAGE_SIZE}`
      : `inncode-groups?page=${data?.page ?? 0}&size=${data?.size ?? SME_PAGE_SIZE}`;
    const response: any = await axiosGet(API_URL, serviceTypes.userplan);

    if (response?.status === responseTypes.success) {
      return response.data;
    } else {
      return thunkApi.rejectWithValue(response.response.data);
    }
  }
);

const getUserDetail = async (mappedUsers: string[]) => {
  const hltIds = mappedUsers.filter((val, index, arr) => arr.indexOf(val) === index);
  const API_URL = `associates/?page=0&size=${hltIds.length}&hltIds=${hltIds}`;
  const response = await axiosGet(API_URL, "user");

  return store.dispatch(setUsers(response.data?.users));
};

export const fetchPlans = async (data: any) => {
  const API_URL = "plans";
  const response: any = await axiosPost(API_URL, "", {
    ...data
  });

  return store.dispatch(setPlans(response?.data));
};

export const fetchMyListDetails = createAsyncThunk("myListDetails", async (data: any, thunkApi: any) => {
  const API_URL = `inncode-groups/${data?.id}?view=${data?.view}&page=${data?.page || 0}&engagement=${
    data?.engagement || ""
  }&inncode=${data.innCode || ""}&size=${data?.size || SME_PAGE_SIZE}`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);

  if (response?.status === responseTypes.success) {
    if (response.data?.mappings) {
      const mappedUsers = response.data?.mappings.filter((el: any) => el.hltId).map((el: any) => el.hltId);

      if (mappedUsers.length) {
        getUserDetail(mappedUsers);
      }
    }

    if (response.data?.planAssigneeMap) {
      const mappedIds = response.data?.planAssigneeMap.map((el: any) => el.cmsPlanId);

      if (mappedIds.length) {
        const props = {
          filter: {
            ids: mappedIds
          },
          pagination: {
            page: 1,
            size: mappedIds.length
          },
          isSortingDisabled: true
        };

        fetchPlans(props);
      }
    }

    return response.data;
  } else {
    return thunkApi.rejectWithValue(response.response.data);
  }
});

export const assignPlanstoInncode = createAsyncThunk("assignPlans", async (data: any, thunkApi) => {
  const { inncode } = data.url;
  const API_URL = `inncode-groups/multiple-assignment?innCode=${inncode}`;
  const { rejectWithValue } = thunkApi;

  const response: any = await axiosPost(API_URL, "userplan", data.payload);
  if (response?.status === responseTypes.accepted) {
    return response;
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const updateInncodeUploadList = createAsyncThunk("updateList", async (data: any, thunkApi) => {
  const API_URL = `inncode-group-mapping`;
  const { rejectWithValue } = thunkApi;

  const response: any = await axiosPost(API_URL, "userplan", data);
  if ([responseTypes.partial_content, responseTypes.created].includes(response?.status)) {
    return response;
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const PlanDetailAsync = createAsyncThunk("details", async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const { planId, innCode, innCodes } = data;
  const API_URL = innCodes
    ? `plans/${planId}?innCode=${innCode}&otherInnCodes=${innCodes}&size=${PAGE_SIZE}`
    : `plans/${planId}?innCode=${innCode}&size=${PAGE_SIZE}`;
  const response: any = await axiosGet(API_URL);
  if (response.status === responseTypes.error) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const uploadMyList = createAsyncThunk("uploadMyList", async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `inncode-groups`;
  const response: any = await axiosFileUpload(API_URL, serviceTypes.userplan, data);

  if (![responseTypes.created, responseTypes.partial_content].includes(response.status)) {
    return rejectWithValue(response.response.data);
  }

  store.dispatch(fetchMyLists({ searchText: "" }));

  return response.data;
});

export const deleteMyList = createAsyncThunk(`deleteMyList`, async (data: any, thunkApi) => {
  const { fulfillWithValue, rejectWithValue } = thunkApi;
  const API_URL = `inncode-groups?innCodeGroupIds=${data}`;
  const response = await axiosDelete(API_URL, serviceTypes.userplan);

  if (response.status === responseTypes.no_content) {
    return fulfillWithValue(data);
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const mapUserToInncode = createAsyncThunk("mapUser", async (data: any, thunkApi) => {
  const API_URL = `inncode-groups/${data?.groupId}`;
  const response: any = await axiosPatch(API_URL, serviceTypes.userplan, data.payload);

  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }

  if (response.data?.mappings) {
    const mappedUsers = response.data?.mappings.filter((el: any) => el.hltId).map((el: any) => el.hltId);

    if (mappedUsers.length) {
      getUserDetail(mappedUsers);
    }
  }

  if (data?.type === "add") {
    const { filters }: any = thunkApi.getState();
    const innCodes = filters?.innCode.join(",") || "";
    const engagements = filters?.engagement.join(",") || "";
    const API_URL = `inncode-groups/${data?.groupId}?view=inncodes&page=0&size=${SME_PAGE_SIZE}&engagement=${engagements}&inncode=${innCodes}`;
    const responseDetails: any = await axiosGet(API_URL, serviceTypes.userplan);

    if (responseDetails?.status === responseTypes.success) {
      response.data.details = responseDetails?.data;
    }
  }

  return response.data;
});

export const deleteMapping = createAsyncThunk(`deleteMapping`, async (data: any, thunkApi) => {
  const { fulfillWithValue, rejectWithValue } = thunkApi;
  const API_URL = `inncode-groups/${data.listId}?mappingIds=${data.mappingIds}`;
  const response = await axiosDelete(API_URL, serviceTypes.userplan);

  if (response.status === responseTypes.no_content) {
    const API_URL = `inncode-groups/${data?.listId}?view=inncodes&page=0&size=${SME_PAGE_SIZE}&engagement=${data?.engagement}&inncode=${data.innCode}`;
    const responseDetails: any = await axiosGet(API_URL, serviceTypes.userplan);

    if (responseDetails?.status === responseTypes.success) {
      data.details = responseDetails?.data;
    }
    return fulfillWithValue(data);
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const fetchPlanDetails = createAsyncThunk("smePlanDetails", async (data: any, thunkApi) => {
  const API_URL = `plans?innCodeGroupMappingIds=${data.id}&innCodeGroupId=${data?.innCodeGroupId}&innCode=${
    data.innCode
  }&size=${PAGE_SIZE}&page=${data.page || 0}`;
  const response: any = await axiosGet(API_URL, "userplan");

  store.dispatch(fetchAdminInncodesAsync(data?.innCode));

  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchPlanStats = createAsyncThunk("planStats", async (data: any, thunkApi) => {
  const API_URL = `inncode-groups/${data?.id}?view=plans&cmsId=${data?.cmsId}&page=${data.page || 0}&engagement=${
    data?.engagement || ""
  }&status=${data.status || ""}&size=${PAGE_SIZE}`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);

  if (response?.status === responseTypes.success) {
    return response.data;
  } else {
    return thunkApi.rejectWithValue(response.response.data);
  }
});

export const SMESlice = createSlice({
  name: "SMEQueries",
  initialState,

  reducers: {
    setShowImportDialog(state, action) {
      state.showImportDialog = action.payload;
    },

    setShowDeleteDialog(state, action) {
      state.showDeleteDialog = action.payload.show;
      state.dialogId = action.payload.dialogId;
    },

    setShowAddInncodeDialog(state, action) {
      state.showAddInncodeDialog = action.payload;
    },

    setUsers: (state, action) => {
      // @ts-ignore
      state.users = [...state.users, ...action.payload];
    },

    setPlans: (state, action) => {
      state.plans = action.payload.plans;
    },

    setSMEFilters: (state, action) => {
      state.filters = action.payload;
    },

    setShowDeleteInncodeDialog(state, action) {
      state.showDeleteInncodeDialog = action.payload.show;
      state.mappingId = action.payload.id;
    },

    setShowImportFailDialog(state, action) {
      state.showImportFailDialog = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPlanStats.rejected, (state, action) => {
        state.status = "failed";
        state.planStats = [];
        state.planStatsTotal = 0;
      })
      .addCase(fetchPlanStats.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchPlanStats.fulfilled, (state, action) => {
        state.status = "idle";
        state.planStats =
          !action.meta.arg?.page || action.meta.arg?.page === 0
            ? action.payload.mappings || []
            : [...state.planStats, ...action.payload.mappings];
        state.planStatsTotal = action.payload.meta?.page.total;
      })
      .addCase(fetchPlanDetails.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchPlanDetails.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchPlanDetails.fulfilled, (state, action) => {
        state.status = "idle";
        state.innCodeStats =
          !action.meta.arg?.page || action.meta.arg?.page === 0
            ? action.payload.userplans || []
            : [...state.innCodeStats, ...action.payload.userplans];
        state.innCodeStatsTotal = action.payload.meta?.page.total;
      })
      .addCase(deleteMapping.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(deleteMapping.fulfilled, (state, action) => {
        const tempLists: any = [...state.myLists.records];
        const indexOfList = tempLists.findIndex((el: any) => el?.id.toString() === action.meta.arg?.listId);

        if (indexOfList >= 0) {
          tempLists[indexOfList].mappingsCount = action.payload?.details?.innCodeGroupMappingCount;
          state.myLists.records = tempLists;
        }

        state.status = "idle";
        state.showDeleteInncodeDialog = false;
        state.myListDetails = {
          ...state.myListDetails,
          ...action.payload?.details,
          mappings: state.myListDetails.mappings.filter((el: any) => el?.id !== state.mappingId)
        };
        state.mappingId = null;
      })
      .addCase(deleteMapping.rejected, (state, action: any) => {
        state.status = "failed";
        state.showDeleteInncodeDialog = false;
        state.mappingId = null;
      })
      .addCase(mapUserToInncode.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(mapUserToInncode.fulfilled, (state, action) => {
        const tempLists: any = [...state.myLists.records];
        const tempMappings: any = [...state.myListDetails.mappings];
        const index = tempMappings.findIndex((el: any) => el?.id === action.payload?.mappings[0]?.id);
        const indexOfList = tempLists.findIndex((el: any) => el?.id.toString() === action.meta.arg?.groupId);

        if (action.payload?.mappings.length) {
          if (index >= 0) {
            tempMappings[index] = action.payload?.mappings[0];
          } else {
            tempLists[indexOfList].mappingsCount = action.payload?.details?.innCodeGroupMappingCount;
            state.myLists.records = tempLists;
            tempMappings.push(action.payload?.mappings[0]);
            state.myListDetails = {
              ...state.myListDetails,
              ...action.payload?.details,
              mappings: tempMappings
            };
          }
        }

        state.status = "idle";
        state.myListDetails.mappings = tempMappings;
        state.showAddInncodeDialog = false;
      })
      .addCase(mapUserToInncode.rejected, (state, action: any) => {
        state.status = "failed";
      })
      .addCase(fetchMyLists.pending, (state) => {
        state.status = "loading";
        state.smeShowLoader = true;
      })
      .addCase(fetchMyLists.fulfilled, (state, action) => {
        state.status = "idle";
        state.myLists.records =
          !action.meta.arg?.page || action.meta.arg?.page === 0
            ? action.payload?.innCodeGroups || []
            : [...state.myLists.records, ...action.payload.innCodeGroups];
        state.myLists.count = action.payload?.page?.total || 0;
        state.smeShowLoader = false;
      })
      .addCase(fetchMyLists.rejected, (state) => {
        state.status = "failed";
        state.smeShowLoader = false;
        state.myLists.records = [];
        state.myLists.count = 0;
        state.myListDetails = {
          id: null,
          mappings: [],
          name: "",
          createdDate: "",
          planAssigneeMap: [],
          assignedPlanCount: 0,
          lowEngagementCount: 0,
          highEngagementCount: 0,
          mediumEngagementCount: 0,
          overDueCardPlansCount: 0,
          innCodeGroupMappingCount: 0,
          unAssignedCardPlansCount: 0,
          meta: {
            page: { total: 0 },
            filters: []
          }
        };
      })
      .addCase(fetchMyListDetails.pending, (state) => {
        state.status = "loading";
        state.smeShowLoader = true;
      })
      .addCase(fetchMyListDetails.fulfilled, (state, action) => {
        state.status = "idle";
        state.myListDetails = action.meta.arg?.page
          ? { ...state.myListDetails, mappings: [...state.myListDetails.mappings, ...action.payload.mappings] }
          : action.payload;
        state.smeShowLoader = false;
      })
      .addCase(fetchMyListDetails.rejected, (state) => {
        state.status = "failed";
        state.smeShowLoader = false;
        state.myListDetails = { ...state.myListDetails, mappings: [] };
      })
      .addCase(uploadMyList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(uploadMyList.fulfilled, (state) => {
        state.status = "idle";
        state.showImportDialog = false;
      })
      .addCase(uploadMyList.rejected, (state, action: any) => {
        state.status = "failed";
      })
      .addCase(updateInncodeUploadList.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(updateInncodeUploadList.fulfilled, (state) => {
        state.smeShowLoader = false;
      })
      .addCase(updateInncodeUploadList.rejected, (state, action: any) => {
        state.smeShowLoader = false;
      })
      .addCase(deleteMyList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteMyList.fulfilled, (state, action) => {
        const tempLists: any = [...state.myLists.records];

        state.status = "idle";
        state.showDeleteDialog = false;
        state.dialogId = null;
        state.myLists = {
          records: tempLists.filter((el: any) => el?.id !== action.meta.arg),
          count: state.myLists.count - 1
        };
      })
      .addCase(deleteMyList.rejected, (state, action: any) => {
        state.status = "failed";
      })
      .addCase(PlanDetailAsync.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(PlanDetailAsync.fulfilled, (state, action: any) => {
        state.status = "idle";
        state.planDetails = action.payload;
        state.smeShowLoader = false;
      })
      .addCase(PlanDetailAsync.rejected, (state) => {
        state.status = "failed";
        state.smeShowLoader = false;
      })
      .addCase(assignPlanstoInncode.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(assignPlanstoInncode.fulfilled, (state, action: any) => {
        state.smeShowLoader = false;
      })
      .addCase(assignPlanstoInncode.rejected, (state) => {
        state.smeShowLoader = false;
      });
  }
});

export const {
  setUsers,
  setPlans,
  setSMEFilters,
  setShowImportDialog,
  setShowDeleteDialog,
  setShowAddInncodeDialog,
  setShowImportFailDialog,
  setShowDeleteInncodeDialog
} = SMESlice.actions;

export default SMESlice.reducer;
